import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import { useSite } from '../contexts/SiteContext';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiLock, FiEye, FiEyeOff } = FiIcons;

const MaintenanceMode = () => {
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState('');
  const { siteConfig } = useSite();

  const handleUnlock = async (e) => {
    e.preventDefault();
    setIsUnlocking(true);
    setError('');

    // Simulate checking passcode
    await new Promise(resolve => setTimeout(resolve, 1000));

    const correctPasscode = siteConfig.maintenance_passcode || 'admin123';
    
    if (passcode === correctPasscode) {
      // Store bypass in localStorage
      localStorage.setItem('maintenance_bypass', 'true');
      window.location.reload();
    } else {
      setError('Λάθος κωδικός πρόσβασης');
      setPasscode('');
    }
    
    setIsUnlocking(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3')] bg-cover bg-center opacity-10"></div>
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-md w-full"
      >
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <ImageWithFallback
                src={siteConfig.logo_url}
                alt={siteConfig.site_name}
                className="w-16 h-16 rounded-full object-contain"
                fallback={
                  <span className="text-white font-bold text-2xl">ΑΟΧ</span>
                }
              />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {siteConfig.site_name}
            </h1>
            <p className="text-white/80 text-sm">
              {siteConfig.site_description}
            </p>
          </motion.div>

          {/* Maintenance Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mb-6"
          >
            <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiSettings} className="w-8 h-8 text-yellow-400 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Υπό Συντήρηση
            </h2>
            <p className="text-white/80 text-sm leading-relaxed">
              Η ιστοσελίδα μας βρίσκεται προσωρινά υπό συντήρηση για βελτιώσεις. 
              Θα είμαστε σύντομα πίσω!
            </p>
          </motion.div>

          {/* Unlock Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            onSubmit={handleUnlock}
            className="space-y-4"
          >
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="h-5 w-5 text-white/60" />
              </div>
              <input
                type={showPasscode ? 'text' : 'password'}
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className="block w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 backdrop-blur-sm"
                placeholder="Κωδικός πρόσβασης"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasscode(!showPasscode)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/60 hover:text-white/80 transition-colors"
              >
                <SafeIcon icon={showPasscode ? FiEyeOff : FiEye} className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-red-300 text-sm flex items-center"
              >
                <SafeIcon icon={FiLock} className="w-4 h-4 mr-2" />
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={isUnlocking}
              className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/30 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUnlocking ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                  Επαλήθευση...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiLock} className="w-5 h-5 mr-2" />
                  Ξεκλείδωμα
                </>
              )}
            </button>
          </motion.form>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-8 pt-6 border-t border-white/20 text-center"
          >
            <p className="text-white/60 text-xs mb-2">
              Για επείγουσες ανάγκες επικοινωνήστε μαζί μας:
            </p>
            <div className="flex justify-center space-x-4 text-white/80 text-sm">
              <span>{siteConfig.phone}</span>
              <span>•</span>
              <span>{siteConfig.email}</span>
            </div>
          </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400/20 rounded-full blur-xl"></div>
        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-400/20 rounded-full blur-xl"></div>
      </motion.div>
    </div>
  );
};

export default MaintenanceMode;